import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const OrgContext = createContext(null);

const LEVEL_RANK = { none: 0, read: 1, write: 2, admin: 3 };

export const useOrg = () => {
  const ctx = useContext(OrgContext);
  if (!ctx) throw new Error("useOrg must be used within OrgProvider");
  return ctx;
};

export const OrgProvider = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const [memberships, setMemberships] = useState([]);
  const [activeOrgId, setActiveOrgIdState] = useState(null);
  const [permissionModules, setPermissionModules] = useState([]);
  const [permissionLevels, setPermissionLevels] = useState([]);
  const [sensitiveActions, setSensitiveActions] = useState([]);
  const [plans, setPlans] = useState({});
  const [effectivePermissions, setEffectivePermissions] = useState({});
  const [effectiveActions, setEffectiveActions] = useState({});
  const [effectiveOrgRole, setEffectiveOrgRole] = useState(null);
  const [effectiveRoleTemplate, setEffectiveRoleTemplate] = useState(null);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    if (!isAuthenticated) return;
    setLoading(true);
    try {
      const res = await api.getMyOrgs();
      const data = res.data || {};
      setMemberships(data.memberships || []);
      setActiveOrgIdState(data.active_organization_id || null);
      setPermissionModules(data.permission_modules || []);
      setPermissionLevels(data.permission_levels || []);
      setSensitiveActions(data.sensitive_actions || []);
      setPlans(data.plans || {});
      setEffectivePermissions(data.effective_permissions || {});
      setEffectiveActions(data.effective_actions || {});
      setEffectiveOrgRole(data.effective_org_role || null);
      setEffectiveRoleTemplate(data.effective_role_template || null);
      setIsSuperAdmin(Boolean(data.is_super_admin));
    } catch (_e) {
      setMemberships([]);
      setActiveOrgIdState(null);
      setEffectivePermissions({});
      setEffectiveActions({});
      setEffectiveOrgRole(null);
      setEffectiveRoleTemplate(null);
      setIsSuperAdmin(false);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (isAuthenticated) refresh();
    else {
      setMemberships([]);
      setActiveOrgIdState(null);
      setIsSuperAdmin(false);
    }
  }, [isAuthenticated, refresh]);

  const setActiveOrg = async (orgId) => {
    if (orgId === activeOrgId) return;
    await api.setActiveOrg(orgId);
    setActiveOrgIdState(orgId);
    // Permissions peuvent changer selon l'org active → refresh.
    await refresh();
  };

  const activeMembership = memberships.find(m => m.organization?.id === activeOrgId) || null;
  const activeOrg = activeMembership?.organization || null;
  const isOrgAdmin = isSuperAdmin || effectiveOrgRole === "admin";

  // Permission helpers — post-P0/P1, plus de fallback legacy.
  // Tous les users existants ont été auto-migrés en admin de leur org.
  const hasLevel = useCallback((module, level) => {
    if (isSuperAdmin) return true;
    if (isOrgAdmin) return true;
    const current = effectivePermissions[module] || "none";
    return (LEVEL_RANK[current] || 0) >= (LEVEL_RANK[level] || 0);
  }, [effectivePermissions, isOrgAdmin, isSuperAdmin]);

  const canRead = useCallback((module) => hasLevel(module, "read"), [hasLevel]);
  const canWrite = useCallback((module) => hasLevel(module, "write"), [hasLevel]);
  const canAdmin = useCallback((module) => hasLevel(module, "admin"), [hasLevel]);
  const canDo = useCallback((action) => {
    if (isSuperAdmin) return true;
    if (isOrgAdmin) return true;
    return Boolean(effectiveActions[action]);
  }, [effectiveActions, isOrgAdmin, isSuperAdmin]);

  const value = useMemo(() => ({
    memberships,
    activeOrg,
    activeOrgId,
    activeMembership,
    isOrgAdmin,
    isSuperAdmin,
    effectivePermissions,
    effectiveActions,
    effectiveOrgRole,
    effectiveRoleTemplate,
    permissionModules,
    permissionLevels,
    sensitiveActions,
    plans,
    loading,
    refresh,
    setActiveOrg,
    canRead,
    canWrite,
    canAdmin,
    canDo,
    hasLevel,
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [
    memberships, activeOrg, activeOrgId, activeMembership, isOrgAdmin, isSuperAdmin,
    effectivePermissions, effectiveActions, effectiveOrgRole, effectiveRoleTemplate,
    permissionModules, permissionLevels, sensitiveActions, plans,
    loading, refresh, canRead, canWrite, canAdmin, canDo, hasLevel,
  ]);

  return <OrgContext.Provider value={value}>{children}</OrgContext.Provider>;
};
